import { useEffect, useMemo, useRef, useState } from 'react';
import { ORIGINAL_GAME, ORIGINAL_GAMES } from '@archon3d/content';
import {
  fingerprint,
  validateGameDefinition,
  definitionAdvice,
  withCardCalibrationReferences,
  validateRoster,
  COMBAT_MODES,
  ENGAGEMENT_RULES,
  type GameDefinition,
  type BoardDef,
  type Roster,
} from '@archon3d/core';
import {
  EditorStore,
  initialEditorState,
  useEditor,
  boardProblems,
  boardHints,
} from './editorStore';
import { BoardCanvas } from './BoardCanvas';
import { EditorTools } from './EditorTools';
import { ArenaEditor } from './ArenaEditor';
import { CharacterEditor } from './CharacterEditor';
import { copyEditableGame } from './copyGame';
import { LegacyBoardLibrary } from './LegacyBoardLibrary';
import { BrowserLibraryNotice } from './BrowserLibraryNotice';
import { NewGameDialog, NewArenaDialog } from './CreationDialog';
import { usePortraits } from './portraits';
import {
  newGameDefinition,
  saveBrowserGame,
  useGameLibrary,
  refreshGameLibrary,
  publicCopies,
  removeBrowserGame,
  type CatalogGame,
} from './gameLibrary';
import { AccountRequestError, accountRequest } from '../net/accounts';
import { ConfirmDialog } from '../ui/menu/ConfirmDialog';
import { DRAFT, DRAFT_LIST, DRAFT_META, draftList, readDrafts, type StoredDrafts } from './drafts';
import { fileName } from './fileName';
import { MAX_IMPORT_BYTES, readImport } from './importFile';
import { editorShortcut } from './shortcuts';
import './gameEditor.css';

/** The roster the editor falls back on while an edit leaves the game's own invalid. */
const ORIGINAL_ROSTER = validateRoster(ORIGINAL_GAME.characters);
const NO_DRAFTS: StoredDrafts = { current: null, entries: [], unreadable: [] };
/** Saves a text as a file the person keeps. */
function download(text: string, name: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function readDraftMeta(id: string): { revision?: string; accountVersion?: string } {
  try {
    const data = JSON.parse(localStorage.getItem(DRAFT_META) ?? 'null');
    if (
      data?.id === id &&
      typeof data.revision === 'string' &&
      typeof data.accountVersion === 'string'
    )
      return data;
  } catch {
    /* A missing revision never grants permission to overwrite an account save. */
  }
  return {};
}
/**
 * Whether the game is as it was last saved to the account. `version` is that save's fingerprint;
 * a draft kept before step 35 holds the whole game instead (editor:bugs#3).
 */
function savedAsIs(version: string | undefined, game: GameDefinition, print: string): boolean {
  return version !== undefined && (version === print || version === JSON.stringify(game));
}
interface Notice {
  text: string;
  /** Something went wrong: said at once to a screen reader, not in its turn. */
  error?: boolean;
  action?: { label: string; run: () => void };
}
function cleanBoard(game: GameDefinition, board: BoardDef): GameDefinition {
  return {
    ...game,
    id: board.id,
    name: board.name,
    board,
    players: {
      min: Math.min(game.players.min, board.factions.length),
      max: Math.min(game.players.max, board.factions.length),
    },
    cellArenas: Object.fromEntries(
      Object.entries(game.cellArenas).filter(([key]) => {
        const [x, y] = key.split(',').map(Number);
        return (
          x! < board.width &&
          y! < board.height &&
          !!board.cells[y!]?.[x!] &&
          board.cells[y!]![x!] !== '#'
        );
      }),
    ),
  };
}
export function GameEditor({
  onPlay,
  onBack,
  savedBoards = [],
  store: kept,
  signedIn = false,
}: {
  onPlay: (id: string) => void;
  onBack: () => void;
  savedBoards?: BoardDef[];
  /**
   * The board editing state the app keeps while the editor is closed, so undo and the chosen tool
   * are there again when it opens on the same game (editor:quality#10).
   */
  store?: EditorStore;
  /** Whether the player is signed in, which saving and publishing need (editor:quality#18). */
  signedIn?: boolean;
}) {
  const [stored] = useState(() => {
    try {
      return readDrafts(localStorage);
    } catch {
      return NO_DRAFTS;
    }
  });
  const [game, setGame] = useState(() => stored.current ?? newGameDefinition());
  // The draft last worked on opens as it was left (editor:quality#10).
  const [selected, setSelected] = useState(() => stored.current !== null);
  const recovered = stored.entries;
  const [drafts, setDrafts] = useState(() => {
    const saved = recovered.map((entry) => entry.definition);
    if (stored.current && !saved.some((g) => g.id === stored.current!.id))
      saved.push(stored.current);
    return saved;
  });
  /** Drafts this version cannot open, kept as they were stored (editor:quality#2). */
  const [unreadable, setUnreadable] = useState(stored.unreadable);
  const [confirming, setConfirming] = useState<'draft' | 'unreadable' | null>(null);
  /**
   * The game as it was when it was loaded, and whether it came from the drafts: one opened and
   * never changed is not kept as a draft when another is chosen (editor:quality#1).
   */
  const loaded = useRef({ json: '', draft: true });
  /** The game whose calibration references are in place (`withCardCalibrationReferences`). */
  const referenced = useRef<string | null>(null);
  const [newGameOpen, setNewGameOpen] = useState(false);
  const [newArenaTemplate, setNewArenaTemplate] = useState<string | null>(null);
  /** An account copy chosen to replace the draft of the same game, until the author confirms. */
  const [replacing, setReplacing] = useState<{
    definition: GameDefinition;
    revision?: string;
  } | null>(null);
  const [store] = useState(() => {
    const character = game.characters.find((c) => !c.summoned)!.id;
    if (!kept) return new EditorStore({ ...initialEditorState(game.board), character });
    // The app's store goes on where it was when it still holds this game's board.
    if (JSON.stringify(kept.getSnapshot().def) !== JSON.stringify(game.board))
      kept.dispatch({ type: 'load', def: game.board });
    if (!game.characters.some((c) => c.id === kept.getSnapshot().character))
      kept.dispatch({ type: 'character', character });
    return kept;
  });
  const state = useEditor(store);
  const library = useGameLibrary();
  const [tab, setTab] = useState<'board' | 'characters' | 'library'>('board');
  const [arenaId, setArenaId] = useState<string | null>(null);
  const [assign, setAssign] = useState(false),
    [assignment, setAssignment] = useState(game.defaultArenaId);
  const [notice, setNoticeState] = useState<Notice | null>(null),
    [busy, setBusy] = useState(false);
  /** What the editor says last, until the next thing happens: '' says nothing. */
  const setNotice = (text: string, more: Omit<Notice, 'text'> = {}) =>
    setNoticeState(text ? { text, ...more } : null);
  const [draftMeta] = useState(() => readDraftMeta(game.id));
  const draftRevisions = useRef(
    new Map([
      [game.id, draftMeta],
      ...recovered.map(
        (entry) =>
          [
            entry.definition.id,
            { revision: entry.revision, accountVersion: entry.accountVersion },
          ] as const,
      ),
    ]),
  );
  const [revision, setRevision] = useState<string | undefined>(draftMeta.revision);
  const [accountVersion, setAccountVersion] = useState<string | undefined>(
    draftMeta.accountVersion,
  );
  const file = useRef<HTMLInputElement>(null);
  // An edit that leaves the roster invalid is a problem to list, never a render that throws and
  // takes the page down (editor:bugs#1): the last valid roster stands in meanwhile.
  const lastRoster = useRef<Roster>(ORIGINAL_ROSTER);
  const checkedRoster = useMemo(() => {
    try {
      return validateRoster(game.characters);
    } catch {
      return null;
    }
  }, [game.characters]);
  if (checkedRoster) lastRoster.current = checkedRoster;
  const roster = checkedRoster ?? lastRoster.current;
  const portraits = usePortraits(roster);
  const characters = game.characters
    .filter((c) => !c.summoned)
    .sort((a, b) => b.tier - a.tier || a.name.localeCompare(b.name))
    .map((c) => c.id);
  // Checked once per change of the game, not at every render (editor:quality#5).
  const problems = useMemo(() => {
    const found = boardProblems(game.board);
    try {
      validateGameDefinition(game);
    } catch (e) {
      found.push((e as Error).message);
    }
    return found;
  }, [game]);
  // What plays but does not do what it says: told, never in the way of saving (content:quality#6).
  const advice = useMemo(() => definitionAdvice(game), [game]);
  const print = useMemo(() => fingerprint(game), [game]);
  /** A game opened from a library and not changed since: nothing to keep as a draft (editor:bugs#3). */
  const pristine = useMemo(
    () => !loaded.current.draft && JSON.stringify(game) === loaded.current.json,
    [game],
  );
  // Only a change to the board is the game's: picking a tool or a brush changes nothing to keep.
  useEffect(
    () =>
      store.subscribe(() => {
        const def = store.getSnapshot().def;
        setGame((g) => (def === g.board ? g : cleanBoard(g, def)));
      }),
    [store],
  );
  useEffect(() => {
    if (!selected) return;
    try {
      if (pristine) {
        localStorage.removeItem(DRAFT);
        localStorage.removeItem(DRAFT_META);
      } else localStorage.setItem(DRAFT, JSON.stringify(game));
    } catch {
      setNotice('Browser storage is full. Export your work now to avoid losing it.', {
        error: true,
      });
    }
  }, [game, selected, pristine]);
  useEffect(() => {
    void refreshGameLibrary().catch(() => {});
  }, []);
  useEffect(() => {
    if (!selected) return;
    draftRevisions.current.set(game.id, { revision, accountVersion });
    try {
      if (!pristine)
        localStorage.setItem(DRAFT_META, JSON.stringify({ id: game.id, revision, accountVersion }));
      const retained = [...drafts.filter((g) => g.id !== game.id), ...(pristine ? [] : [game])];
      localStorage.setItem(
        DRAFT_LIST,
        JSON.stringify(draftList(retained, (id) => draftRevisions.current.get(id), unreadable)),
      );
    } catch {
      setNotice('Could not retain the account revision. Export your draft before leaving.', {
        error: true,
      });
    }
  }, [game, revision, accountVersion, selected, drafts, unreadable, pristine]);
  /**
   * Opens a game. The one open until now is kept as a draft if it is one or was changed, unless
   * `replace` says the game opened is the account's copy of it, chosen over the draft.
   */
  const load = (definition: GameDefinition, rev?: string, fromDraft = false, replace = false) => {
    if (selected && !(replace && game.id === definition.id)) {
      const keep = loaded.current.draft || JSON.stringify(game) !== loaded.current.json;
      setDrafts((old) => [...old.filter((g) => g.id !== game.id), ...(keep ? [game] : [])]);
    }
    if (replace) setDrafts((old) => old.filter((g) => g.id !== definition.id));
    setSelected(true);
    const copy = structuredClone(definition);
    loaded.current = { json: JSON.stringify(copy), draft: fromDraft };
    setGame(copy);
    store.dispatch({ type: 'load', def: copy.board });
    setRevision(rev);
    setAccountVersion(rev ? fingerprint(copy) : undefined);
    setArenaId(null);
    setAssign(false);
    setAssignment(copy.defaultArenaId);
    setTab('board');
    setNotice('');
  };
  /** Writes the draft list as it is now, outside the effect that keeps the current game's. */
  const writeDrafts = (list: GameDefinition[], kept: unknown[]) => {
    try {
      localStorage.setItem(
        DRAFT_LIST,
        JSON.stringify(draftList(list, (id) => draftRevisions.current.get(id), kept)),
      );
      return true;
    } catch {
      setNotice('Could not update the drafts in this browser.', { error: true });
      return false;
    }
  };
  /** The author throws the current draft away (editor:quality#1); saved copies stay. */
  const discardDraft = () => {
    const rest = drafts.filter((g) => g.id !== game.id);
    draftRevisions.current.delete(game.id);
    if (!writeDrafts(rest, unreadable)) return;
    try {
      localStorage.removeItem(DRAFT);
      localStorage.removeItem(DRAFT_META);
    } catch {
      /* The list no longer holds it; a stale current draft is read as one more draft. */
    }
    const blank = newGameDefinition();
    setDrafts(rest);
    setSelected(false);
    setGame(blank);
    store.dispatch({ type: 'load', def: blank.board });
    loaded.current = { json: JSON.stringify(blank), draft: true };
    setRevision(undefined);
    setAccountVersion(undefined);
    setArenaId(null);
    setTab('board');
    setNotice(`Discarded the draft “${game.name}”.`);
  };
  /** The author removes the drafts this version cannot open, their backup downloaded or not. */
  const removeUnreadable = () => {
    const list = selected ? [...drafts.filter((g) => g.id !== game.id), game] : drafts;
    if (!writeDrafts(list, [])) return;
    setUnreadable([]);
    setNotice('Removed the drafts this version of the editor could not open.');
  };
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      // Saved from another tab or device since it was opened here: the account's copy is offered.
      const stale = e instanceof AccountRequestError && e.status === 409;
      setNotice((e as Error).message, {
        error: true,
        ...(stale && revision
          ? { action: { label: 'Load account copy', run: loadAccountCopy } }
          : {}),
      });
    } finally {
      setBusy(false);
    }
  };
  /** The copy of this game saved to the account, to replace the draft once the author confirms. */
  const loadAccountCopy = () =>
    void run(async () => {
      const saved = (await refreshGameLibrary()).own.find((e) => e.definition.id === game.id);
      if (!saved) throw new Error('This game is no longer saved to your account.');
      setReplacing({ definition: saved.definition, revision: saved.revision });
    });
  /** Why Save cannot be pressed now, or null when it can. */
  const saveBlocked = !selected
    ? 'Select or create a game first'
    : !signedIn
      ? 'Sign in from the main menu to save to your account'
      : problems.length
        ? 'Fix the problems listed below first'
        : null;
  /** Why Publish cannot be pressed now, or null when it can: one reason each (editor:quality#18). */
  const publishBlocked = !signedIn
    ? 'Sign in from the main menu to publish'
    : !revision
      ? 'Save the game to your account first'
      : problems.length
        ? 'Fix the problems listed below first'
        : !savedAsIs(accountVersion, game, print)
          ? 'Save your changes to your account before publishing them'
          : null;
  const save = () => {
    if (problems.length) throw new Error(problems.join(' '));
    saveBrowserGame(game);
    setNotice(`Saved “${game.name}” with all arenas and abilities in this browser.`);
  };
  const saveAccount = async () => {
    if (problems.length) throw new Error(problems.join(' '));
    const entry = await accountRequest<{ item: { revision: string } }>('games', {
      action: 'save',
      definition: game,
      expectedRevision: revision,
    });
    setRevision(entry.item.revision);
    setAccountVersion(print);
    await refreshGameLibrary();
    setNotice('Saved privately to your account. Draft recovery remains on this device.');
  };
  /**
   * A copy of a game to edit: a draft from the start when made on purpose (Duplicate, Import), or
   * one kept only once changed when it is an original looked at (editor:quality#1).
   */
  const copyGame = (g: GameDefinition, asDraft = false) => {
    const copy = copyEditableGame(g, `${g.name.slice(0, 93)} (copy)`);
    load(copy, undefined, asDraft);
  };
  const libraryKey = (e: CatalogGame) => `lib:${e.source}:${e.owner ?? ''}:${e.definition.id}`;
  /**
   * Opens a game saved to the account or in this browser. A draft of it kept here that differs
   * from it is replaced only once the author confirms (editor:quality#6); one the same goes.
   */
  const openSaved = (entry: CatalogGame) => {
    const id = entry.definition.id;
    const draft = selected && game.id === id ? game : drafts.find((g) => g.id === id);
    if (draft && JSON.stringify(draft) !== JSON.stringify(entry.definition))
      setReplacing({ definition: entry.definition, revision: entry.revision });
    else load(entry.definition, entry.revision, false, !!draft);
  };
  const chosenArena = game.arenas.find((a) => a.id === arenaId);
  // Undo and redo on the board, and Save, from the keyboard; a text field keeps the browser's own
  // (editor:quality#14). The arena editor has its own undo and takes the keys while it is open.
  const onShortcut = useRef<(e: KeyboardEvent) => void>(() => undefined);
  onShortcut.current = (e) => {
    const shortcut = editorShortcut(e);
    if (shortcut === 'save') {
      e.preventDefault();
      if (busy) return;
      if (saveBlocked) setNotice(`Not saved: ${saveBlocked.toLowerCase()}.`, { error: true });
      else void run(saveAccount);
    } else if (shortcut && selected && tab === 'board' && !arenaId) {
      e.preventDefault();
      store.dispatch({ type: shortcut });
    }
  };
  useEffect(() => {
    const listener = (e: KeyboardEvent) => onShortcut.current(e);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);
  useEffect(() => {
    const bridge = {
      read: () => (selected ? structuredClone(game) : null),
      write: (value: unknown) => {
        if (!selected) throw new Error('Select or create a game first');
        const next = validateGameDefinition(value);
        if (
          next.id !== game.id ||
          next.board.grid !== game.board.grid ||
          next.board.width !== game.board.width ||
          next.board.height !== game.board.height
        )
          throw new Error('Game identity and board geometry are fixed; create a new game instead');
        if (
          next.defaultArenaId !== game.defaultArenaId ||
          JSON.stringify(next.arenas.find((a) => a.id === next.defaultArenaId)) !==
            JSON.stringify(game.arenas.find((a) => a.id === game.defaultArenaId))
        )
          throw new Error('Default Arena is protected');
        for (const arena of next.arenas) {
          const old = game.arenas.find((a) => a.id === arena.id);
          if (old && (old.width !== arena.width || old.height !== arena.height))
            throw new Error('Arena geometry is fixed');
        }
        setGame(structuredClone(next));
        store.dispatch({ type: 'load', def: next.board });
      },
    };
    window.__arenaEditor = bridge;
    return () => {
      if (window.__arenaEditor === bridge) delete window.__arenaEditor;
    };
  }, [game, selected, store]);
  return (
    <div className="editor game-editor" data-testid="game-editor">
      {newGameOpen && (
        <NewGameDialog
          library={library}
          onCancel={() => setNewGameOpen(false)}
          onCreate={(g) => {
            // Made on purpose: a draft from the start, changed yet or not.
            load(g, undefined, true);
            setNewGameOpen(false);
          }}
        />
      )}
      {replacing && (
        <ConfirmDialog
          title="Open the copy saved to your account?"
          detail={`The draft of “${replacing.definition.name}” kept in this browser is replaced by it, and the draft’s changes are lost.`}
          confirmLabel="Open saved copy"
          onConfirm={() => {
            const chosen = replacing;
            setReplacing(null);
            load(chosen.definition, chosen.revision, false, true);
          }}
          onCancel={() => setReplacing(null)}
        />
      )}
      {newArenaTemplate !== null && (
        <NewArenaDialog
          arenas={game.arenas}
          tilesets={game.tilesets}
          initialTemplate={newArenaTemplate}
          onCancel={() => setNewArenaTemplate(null)}
          onCreate={(a) => {
            setGame((g) => ({ ...g, arenas: [...g.arenas, a] }));
            setArenaId(a.id);
            setNewArenaTemplate(null);
          }}
        />
      )}
      <div className="editor-head">
        <h2 className="menu-heading">Game editor</h2>
        <div className="menu-actions">
          <label>
            Edit game{' '}
            <select
              aria-label="Edit game"
              disabled={busy}
              value={selected ? 'current' : ''}
              onChange={(e) => {
                const value = e.target.value;
                const draft = drafts.find((g) => `draft:${g.id}` === value);
                const entry = library.find((g) => libraryKey(g) === value);
                if (draft) {
                  const meta = draftRevisions.current.get(draft.id) ?? readDraftMeta(draft.id);
                  load(draft, meta.revision, true);
                  setAccountVersion(meta.accountVersion);
                } else if (entry) {
                  if (entry.source === 'Original' || entry.source === 'Public')
                    copyGame(entry.definition);
                  else openSaved(entry);
                }
              }}
            >
              <option value="" disabled>
                Select a game…
              </option>
              {selected && (
                <option value="current">
                  {game.name || 'Untitled game'} · {pristine ? 'Open, unchanged' : 'Current draft'}
                </option>
              )}
              {drafts
                .filter((g) => !selected || g.id !== game.id)
                .map((g) => (
                  <option key={g.id} value={`draft:${g.id}`}>
                    {g.name || 'Untitled game'} · Draft
                  </option>
                ))}
              {library
                .filter((e) => !(selected && pristine && e.definition.id === game.id))
                .map((e) => (
                  <option key={libraryKey(e)} value={libraryKey(e)}>
                    {e.definition.name} · {e.source}
                    {e.source === 'Original' || e.source === 'Public' ? ' (copy)' : ''}
                  </option>
                ))}
            </select>
          </label>
          <button disabled={busy} onClick={() => setNewGameOpen(true)}>
            New game…
          </button>
          <button
            disabled={!selected || busy}
            onClick={() => setConfirming('draft')}
            data-testid="discard-draft"
          >
            Discard draft
          </button>
          <button
            disabled={busy || !!saveBlocked}
            title={saveBlocked ?? 'Save this game privately to your account (Ctrl+S)'}
            onClick={() => void run(saveAccount)}
          >
            Save
          </button>
          <button
            disabled={!selected || !!problems.length}
            className="primary"
            onClick={() =>
              void run(async () => {
                save();
                onPlay(game.id);
              })
            }
          >
            Play game
          </button>
          <button onClick={onBack}>Back</button>
        </div>
      </div>
      <BrowserLibraryNotice />
      {notice && (
        <p
          className={`editor-notice${notice.error ? ' error' : ''}`}
          role={notice.error ? 'alert' : 'status'}
          data-testid="editor-notice"
        >
          {notice.text}
          {notice.action && (
            <>
              {' '}
              <button onClick={notice.action.run}>{notice.action.label}</button>
            </>
          )}
          <button className="notice-dismiss" aria-label="Dismiss" onClick={() => setNotice('')}>
            ×
          </button>
        </p>
      )}
      {unreadable.length > 0 && (
        <div className="hint" role="status" data-testid="unreadable-drafts">
          <p>
            {unreadable.length === 1
              ? 'A draft could not be opened by this version of the editor.'
              : `${unreadable.length} drafts could not be opened by this version of the editor.`}{' '}
            They are kept as they were stored; download them to keep a copy.
          </p>
          <button
            onClick={() =>
              download(JSON.stringify(unreadable, null, 2), 'rpg-arena-editor-drafts-backup.json')
            }
          >
            Download draft backup
          </button>{' '}
          <button onClick={() => setConfirming('unreadable')}>Remove them</button>
        </div>
      )}
      {confirming === 'draft' && (
        <ConfirmDialog
          title="Discard this draft?"
          detail="Changes not saved to this browser’s games or your account are lost."
          confirmLabel="Discard"
          onConfirm={() => {
            setConfirming(null);
            discardDraft();
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
      {confirming === 'unreadable' && (
        <ConfirmDialog
          title="Remove the drafts this version cannot open?"
          detail="Download the backup first to keep a copy."
          confirmLabel="Remove"
          onConfirm={() => {
            setConfirming(null);
            removeUnreadable();
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
      {selected && (
        <p className="hint">
          {game.name} · {game.board.width}×{game.board.height} · {game.board.placements.length}{' '}
          pieces · {game.arenas.length} arenas. Draft changes are kept in this browser
          automatically.
          {!signedIn && ' Sign in from the main menu to save the game to your account as well.'}
        </p>
      )}
      <nav className="menu-actions" aria-label="Editor sections">
        {(['board', 'characters', 'library'] as const).map((t) => (
          <button
            key={t}
            disabled={!selected}
            className={selected && tab === t && !arenaId ? 'active' : ''}
            onClick={() => {
              setTab(t);
              setArenaId(null);
            }}
          >
            {t === 'board'
              ? 'Edit board & arenas'
              : t === 'characters'
                ? 'Character abilities'
                : 'Publish'}
          </button>
        ))}
      </nav>
      {selected && (
        <>
          {newArenaTemplate !== null ? (
            <section className="arena-editor">
              <h3>Arena map editor</h3>
              <p>Create your arena to begin editing.</p>
            </section>
          ) : chosenArena ? (
            <ArenaEditor
              key={chosenArena.id}
              arena={chosenArena}
              tileset={game.tilesets.find((t) => t.id === chosenArena.tileset)!}
              readOnly={chosenArena.id === game.defaultArenaId}
              onBack={() => setArenaId(null)}
              onChange={(a) =>
                setGame((g) => ({
                  ...g,
                  arenas: g.arenas.map((old) => (old.id === a.id ? a : old)),
                }))
              }
            />
          ) : tab === 'characters' ? (
            <CharacterEditor
              resetTemplate={
                ORIGINAL_GAMES.find(
                  (g) => (g.board.grid ?? 'square') === (game.board.grid ?? 'square'),
                ) ?? ORIGINAL_GAMES[0]
              }
              characters={game.characters}
              portraits={portraits}
              onChange={(characters) => {
                // The calibration's references are the characters as measured; they are set
                // once, before a game's first edit, not copied again at every keystroke.
                const base =
                  referenced.current === game.id ? game : withCardCalibrationReferences(game);
                referenced.current = game.id;
                setGame({ ...base, characters });
              }}
            />
          ) : tab === 'library' ? (
            <section>
              <h3>Save, share & publish</h3>
              <p className="hint">
                Save stores the complete game privately in your account, up to 20 saves and
                publishes a day. Sign in from the main menu to save. Publishing creates a public
                snapshot; save and publish again to update it. Existing matches keep their original
                snapshot. Custom games are unranked.
              </p>
              <div className="menu-actions">
                <button
                  disabled={busy || !!publishBlocked}
                  title={publishBlocked ?? 'Publish the version last saved to your account'}
                  onClick={() =>
                    void run(async () => {
                      await accountRequest('games', {
                        action: 'publish',
                        id: game.id,
                        expectedRevision: revision,
                      });
                      await refreshGameLibrary();
                      setNotice('Published the last account-saved version.');
                    })
                  }
                >
                  Publish / update public version
                </button>
                <button
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await refreshGameLibrary();
                      setNotice(
                        'Library refreshed. Load an account game to edit its latest revision.',
                      );
                    })
                  }
                >
                  Refresh library
                </button>
                <button
                  // Named after the game, not its generated id (editor:quality#20).
                  onClick={() =>
                    download(
                      JSON.stringify(game, null, 2),
                      `${fileName(game.name) || game.id}.json`,
                    )
                  }
                >
                  Export game
                </button>
                <button onClick={() => file.current?.click()}>Import game / board</button>
                <input
                  ref={file}
                  type="file"
                  accept=".json"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f)
                      void run(async () => {
                        // Too large a file is refused before it is read (editor:quality#19).
                        const text = f.size > MAX_IMPORT_BYTES ? '' : await f.text();
                        copyGame(readImport(text, f.size), true);
                        setNotice('Imported as an independent copy.');
                      });
                    e.target.value = '';
                  }}
                />
              </div>
              <ul className="saved-boards">
                {library.map((entry) => (
                  <li key={`${entry.source}-${entry.owner ?? ''}-${entry.definition.id}`}>
                    <span className="saved-name">
                      <strong>{entry.definition.name}</strong>
                      <small>
                        {entry.source}
                        {entry.author ? ` · ${entry.author}` : ''}
                      </small>
                    </span>
                    <button
                      onClick={() =>
                        entry.source === 'Own' || entry.source === 'Browser'
                          ? openSaved(entry)
                          : copyGame(entry.definition, true)
                      }
                    >
                      {entry.source === 'Own' || entry.source === 'Browser'
                        ? 'Edit'
                        : 'Copy to edit'}
                    </button>
                    <button onClick={() => copyGame(entry.definition, true)}>Duplicate</button>
                    {(entry.source === 'Own' || entry.source === 'Browser') && (
                      <button
                        disabled={busy}
                        onClick={() => {
                          if (
                            !confirm(
                              `Delete saved “${entry.definition.name}”? Published copies are removed separately.`,
                            )
                          )
                            return;
                          void run(async () => {
                            if (entry.source === 'Own') {
                              await accountRequest('games', {
                                action: 'remove',
                                id: entry.definition.id,
                                expectedRevision: entry.revision,
                                published: false,
                              });
                              await refreshGameLibrary();
                            }
                            removeBrowserGame(entry.definition.id);
                            if (entry.definition.id === game.id) setRevision(undefined);
                            setNotice('Saved copy deleted. The current draft is retained.');
                          });
                        }}
                      >
                        Delete save
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {publicCopies().map((entry) => (
                <p key={entry.id}>
                  {entry.definition.name} · published{' '}
                  <button
                    disabled={busy}
                    onClick={() => {
                      if (
                        confirm('Remove this game from the public list? Your private save remains.')
                      )
                        void run(async () => {
                          await accountRequest('games', {
                            action: 'remove',
                            id: entry.id,
                            expectedRevision: entry.revision,
                            published: true,
                          });
                          await refreshGameLibrary();
                          setNotice('Public version removed.');
                        });
                    }}
                  >
                    Unpublish
                  </button>
                </p>
              ))}
              {savedBoards.length > 0 && (
                <section>
                  <h3>Legacy browser boards</h3>
                  {savedBoards.map((board) => (
                    <button
                      key={board.id}
                      onClick={() =>
                        copyGame(
                          {
                            ...newGameDefinition(board.name),
                            id: board.id,
                            board,
                            players: { min: 2, max: board.factions.length },
                          },
                          true,
                        )
                      }
                    >
                      Import {board.name}
                    </button>
                  ))}
                </section>
              )}
              <LegacyBoardLibrary
                onImport={(board) =>
                  copyGame(
                    {
                      ...newGameDefinition(board.name),
                      id: board.id,
                      board,
                      players: { min: 2, max: board.factions.length },
                    },
                    true,
                  )
                }
              />
            </section>
          ) : (
            <div className="editor-body">
              <div className="editor-stage">
                <div className="menu-actions">
                  <button title="Ctrl+Z" onClick={() => store.dispatch({ type: 'undo' })}>
                    Undo board
                  </button>
                  <button
                    title="Ctrl+Shift+Z or Ctrl+Y"
                    onClick={() => store.dispatch({ type: 'redo' })}
                  >
                    Redo board
                  </button>
                  <label>
                    <input
                      type="checkbox"
                      checked={assign}
                      onChange={(e) => setAssign(e.target.checked)}
                    />{' '}
                    Assign arenas to cells
                  </label>
                </div>
                {assign && (
                  <label>
                    Click cells to assign{' '}
                    <select
                      aria-label="Cell arena"
                      value={assignment}
                      onChange={(e) => setAssignment(e.target.value)}
                    >
                      {game.arenas.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <BoardCanvas
                  def={state.def}
                  roster={roster}
                  portraits={portraits}
                  arenaLabels={Object.fromEntries(
                    Object.entries(game.cellArenas).map(([cell, id]) => [
                      cell,
                      game.arenas.find((a) => a.id === id)?.name ?? 'Default Arena',
                    ]),
                  )}
                  tool={state.tool}
                  wallFrom={state.wallFrom}
                  // Arenas are assigned, and cells painted, a stroke at a time (editor:quality#12).
                  drag={assign || state.tool === 'paint'}
                  onCell={(x, y, stroke) => {
                    if (assign)
                      setGame((g) => {
                        if (g.board.cells[y]?.[x] === '#') return g;
                        const cellArenas = { ...g.cellArenas };
                        if (assignment === g.defaultArenaId) delete cellArenas[`${x},${y}`];
                        else cellArenas[`${x},${y}`] = assignment;
                        return { ...g, cellArenas };
                      });
                    else store.dispatch({ type: 'cell', x, y, ...(stroke ? { stroke } : {}) });
                  }}
                />
                <p className="hint">
                  Circles are power points. Cyan outlines mark custom arenas (hover for names).
                  Every cell uses Default Arena unless assigned otherwise.{' '}
                  {Object.keys(game.cellArenas).length} arena overrides.
                </p>
                <section className="editor-section">
                  <h3>Associated arena maps</h3>
                  <button
                    disabled={game.arenas.length >= 12}
                    onClick={() => setNewArenaTemplate('')}
                  >
                    Add arena
                  </button>
                  <ul className="saved-boards">
                    {game.arenas.map((a) => (
                      <li key={a.id}>
                        <strong>
                          {a.name}
                          {a.id === game.defaultArenaId ? ' · protected' : ''}
                        </strong>
                        <button onClick={() => setArenaId(a.id)}>
                          {a.id === game.defaultArenaId ? 'View' : 'Edit'}
                        </button>
                        <button
                          disabled={game.arenas.length >= 12}
                          onClick={() => setNewArenaTemplate(a.id)}
                        >
                          Copy
                        </button>
                        {a.id !== game.defaultArenaId && (
                          <button
                            onClick={() => {
                              if (
                                confirm(
                                  'Delete this arena? Its assigned cells will use Default Arena.',
                                )
                              ) {
                                setGame({
                                  ...game,
                                  arenas: game.arenas.filter((b) => b.id !== a.id),
                                  cellArenas: Object.fromEntries(
                                    Object.entries(game.cellArenas).filter(([, id]) => id !== a.id),
                                  ),
                                });
                                setAssignment(game.defaultArenaId);
                              }
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
              <aside className="editor-side-panel">
                <fieldset>
                  <legend>Allowed player count</legend>
                  {(['min', 'max'] as const).map((key) => (
                    <label key={key}>
                      {key === 'min' ? 'Minimum' : 'Maximum'}
                      <select
                        aria-label={`${key} players`}
                        value={game.players[key]}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          setGame({
                            ...game,
                            players:
                              key === 'min'
                                ? { min: n, max: Math.max(n, game.players.max) }
                                : { min: Math.min(n, game.players.min), max: n },
                          });
                        }}
                      >
                        {Array.from(
                          { length: game.board.factions.length - 1 },
                          (_, i) => i + 2,
                        ).map((n) => (
                          <option key={n}>{n}</option>
                        ))}
                      </select>
                    </label>
                  ))}
                </fieldset>
                <fieldset>
                  <legend>Allowed combats</legend>
                  {COMBAT_MODES.map((mode) => {
                    const rules = game.combatRules ?? {
                      modes: [...COMBAT_MODES],
                      defaultMode: 'card' as const,
                      engagements: ['immediate'] as const,
                      defaultEngagement: 'immediate' as const,
                    };
                    const checked = rules.modes.includes(mode);
                    return (
                      <label className="check" key={mode}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={checked && rules.modes.length === 1}
                          onChange={(e) => {
                            const modes = e.target.checked
                              ? [...rules.modes, mode]
                              : rules.modes.filter((m) => m !== mode);
                            setGame({
                              ...game,
                              combatRules: {
                                ...rules,
                                modes,
                                defaultMode: modes.includes(rules.defaultMode)
                                  ? rules.defaultMode
                                  : modes[0]!,
                              },
                            });
                          }}
                        />
                        {mode[0]!.toUpperCase() + mode.slice(1)}
                      </label>
                    );
                  })}
                </fieldset>
                <fieldset>
                  <legend>Allowed engagement timing</legend>
                  {ENGAGEMENT_RULES.map((engagement) => {
                    const rules = game.combatRules ?? {
                      modes: [...COMBAT_MODES],
                      defaultMode: 'card' as const,
                      engagements: ['immediate'] as ('immediate' | 'muster')[],
                      defaultEngagement: 'immediate' as const,
                    };
                    const checked = rules.engagements.includes(engagement);
                    return (
                      <label className="check" key={engagement}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={checked && rules.engagements.length === 1}
                          onChange={(e) => {
                            const engagements = e.target.checked
                              ? [...rules.engagements, engagement]
                              : rules.engagements.filter((item) => item !== engagement);
                            setGame({
                              ...game,
                              combatRules: {
                                ...rules,
                                engagements,
                                defaultEngagement: engagements.includes(rules.defaultEngagement)
                                  ? rules.defaultEngagement
                                  : engagements[0]!,
                              },
                            });
                          }}
                        />
                        {engagement === 'muster' ? 'Muster (multi-faction)' : 'Immediate'}
                      </label>
                    );
                  })}
                </fieldset>
                <EditorTools
                  state={state}
                  store={store}
                  roster={roster}
                  characters={characters}
                  fixedGeometry
                />
              </aside>
            </div>
          )}
          <section data-testid="editor-problems">
            {problems.length === 0 && <p className="hint ok">Ready to play.</p>}
            {[...boardHints(game.board), ...advice].map((h) => (
              <p key={h} className="hint warn" data-testid="editor-hint">
                {h}
              </p>
            ))}
          </section>
          {!!problems.length && (
            <section className="editor-status" data-testid="game-problems">
              <h3>Before saving / playing</h3>
              <ul className="problems">
                {[...new Set(problems)].map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
